import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Chip,
  Grid,
  Tab,
  Tabs,
  Card,
  CardContent,
  Breadcrumbs,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  CalendarToday as CalendarTodayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Class as ClassIcon,
  Schedule as CoachingIcon,
  Repeat as CoachingProgramIcon,
  Sports as GroupPlayIcon,
  Games as AmenityIcon,
  Event as EventIcon,
  Today as TodayIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  ViewModule as ViewModuleIcon,
  DateRange as DateRangeIcon,
  MoneyOff as MoneyOffIcon,
  AttachMoney as AttachMoneyIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import {
  classAPI,
  classInstanceAPI,
  coachingSessionAPI,
  coachingProgramAPI,
  coachingProgramInstanceAPI,
  amenityScheduleAPI,
  amenityAPI,
  eventAPI,
  classTypeAPI,
  groupPlayAPI
} from '../../services/api';
import { formatDateForInput, formatDate, parseDateOnlyString, isDateOnly, formatTimeString } from '../../utils/dateUtils';
import BookAmenityDialog from '../amenities/BookAmenityDialog';
import { Amenity, UserRole } from '../../types';
import { useAuth } from '../../context/AuthContext';

// Types for calendar data
interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  date: string;
  type: CalendarEventType;
  status?: string;
  capacity?: number;
  enrolledCount?: number;
  location?: string;
  instructor?: string;
  cost?: number;
  metadata?: any;
  classType?: string; // Class type name for color coding
  hasMissingBookings?: boolean; // True if event is missing amenity bookings
  missingVenues?: string[]; // IDs of venues missing bookings
}

enum CalendarEventType {
  CLASS = 'class',
  COACHING_SESSION = 'coaching_session',
  COACHING_PROGRAM = 'coaching_program',
  AMENITY = 'amenity',
  EVENT = 'event',
  GROUP_PLAY = 'group_play'
}

enum CalendarViewType {
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly'
}

const UniversalCalendar: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Calendar container ref for scrolling
  const calendarContainerRef = useRef<HTMLDivElement>(null);

  // Load saved preferences from localStorage
  const loadSavedPreferences = () => {
    const savedType = localStorage.getItem('calendarType');
    const savedView = localStorage.getItem('calendarView');
    const savedDate = localStorage.getItem('calendarDate');
    const savedAmenityId = localStorage.getItem('calendarAmenityId');

    return {
      type: savedType ? (savedType as CalendarEventType) : CalendarEventType.CLASS,
      view: savedView ? (savedView as CalendarViewType) : CalendarViewType.DAILY,
      date: savedDate ? new Date(savedDate) : new Date(),
      amenityId: savedAmenityId || ''
    };
  };

  const savedPrefs = loadSavedPreferences();

  // State management
  const [selectedType, setSelectedType] = useState<CalendarEventType>(savedPrefs.type);
  const [selectedView, setSelectedView] = useState<CalendarViewType>(savedPrefs.view);
  const [selectedDate, setSelectedDate] = useState(savedPrefs.date);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [availableAmenities, setAvailableAmenities] = useState<any[]>([]);
  const [selectedAmenityId, setSelectedAmenityId] = useState<string>(savedPrefs.amenityId);
  const [openDatePicker, setOpenDatePicker] = useState<boolean>(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<string>('');
  const [amenitySlotDuration, setAmenitySlotDuration] = useState<number>(30); // For amenity view
  const [classTypes, setClassTypes] = useState<any[]>([]); // Class types for color coding
  const [classTypeColorMap, setClassTypeColorMap] = useState<Record<string, string>>({}); // Map of class type to color

  // Booking dialog state
  const [openBookingDialog, setOpenBookingDialog] = useState(false);
  const [bookingDialogAmenity, setBookingDialogAmenity] = useState<Amenity | null>(null);
  const [bookingDialogDate, setBookingDialogDate] = useState<Date | null>(null);
  const [bookingDialogSlot, setBookingDialogSlot] = useState<{ startTime: string; endTime: string } | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Calendar type options
  const calendarTypes = [
    { value: CalendarEventType.CLASS, label: 'Classes', icon: <ClassIcon /> },
    { value: CalendarEventType.COACHING_SESSION, label: 'Coaching Sessions', icon: <CoachingIcon /> },
    { value: CalendarEventType.COACHING_PROGRAM, label: 'Coaching Programs', icon: <CoachingProgramIcon /> },
    { value: CalendarEventType.GROUP_PLAY, label: 'Group Play', icon: <GroupPlayIcon /> },
    { value: CalendarEventType.AMENITY, label: 'Amenities', icon: <AmenityIcon /> },
    { value: CalendarEventType.EVENT, label: 'Events', icon: <EventIcon /> }
  ];

  // Calendar view options
  const viewTypes = [
    { value: CalendarViewType.DAILY, label: 'Daily', icon: <ViewDayIcon /> },
    { value: CalendarViewType.WEEKLY, label: 'Weekly', icon: <ViewWeekIcon /> },
    { value: CalendarViewType.MONTHLY, label: 'Monthly', icon: <ViewModuleIcon /> }
  ];

  // Helper function to calculate GCD (Greatest Common Divisor)
  const gcd = (a: number, b: number): number => {
    return b === 0 ? a : gcd(b, a % b);
  };

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('calendarType', selectedType);
  }, [selectedType]);

  useEffect(() => {
    localStorage.setItem('calendarView', selectedView);
  }, [selectedView]);

  useEffect(() => {
    localStorage.setItem('calendarDate', selectedDate.toISOString());
  }, [selectedDate]);

  useEffect(() => {
    if (selectedAmenityId) {
      localStorage.setItem('calendarAmenityId', selectedAmenityId);
    }
  }, [selectedAmenityId]);

  // Fetch amenities list when component mounts
  useEffect(() => {
    fetchAmenities();
    fetchClassTypes();
  }, []);

  const fetchClassTypes = async () => {
    try {
      const response = await classTypeAPI.getAll();
      const types = response.data || [];
      setClassTypes(types);
      
      // Build color map with lowercase keys for case-insensitive lookup
      const colorMap: Record<string, string> = {};
      types.forEach((classType: any) => {
        if (classType.name && classType.color) {
          colorMap[classType.name.toLowerCase()] = classType.color;
        }
      });
      setClassTypeColorMap(colorMap);
    } catch (error: any) {
      console.error('Error fetching class types:', error);
    }
  };

  // Fetch calendar data when dependencies change
  useEffect(() => {
    fetchCalendarData();
  }, [selectedType, selectedDate, selectedView, selectedAmenityId]);

  // Auto-scroll to current time on mount
  useEffect(() => {
    if (selectedView === CalendarViewType.DAILY && selectedDate.toDateString() === new Date().toDateString()) {
      setTimeout(() => {
        scrollToCurrentTime();
      }, 500);
    }
  }, [calendarEvents, selectedView, selectedDate]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle shortcuts when not in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.key) {
        case 't':
        case 'T':
          goToToday();
          break;
        case 'j':
        case 'J':
          openDatePickerDialog();
          break;
        case 'ArrowLeft':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            navigateDate('prev');
          }
          break;
        case 'ArrowRight':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            navigateDate('next');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedView, selectedDate]);

  // Check for missing amenity bookings (Admin only, for coaching/group play events)
  useEffect(() => {
    if (!user || (user.role !== UserRole.ADMIN && user.role !== UserRole.SUPER_ADMIN && user.role !== UserRole.EMPLOYEE)) {
      return; // Only check for admin/employee users
    }

    // Only check for specific event types
    if (selectedType !== CalendarEventType.COACHING_SESSION &&
        selectedType !== CalendarEventType.COACHING_PROGRAM &&
        selectedType !== CalendarEventType.GROUP_PLAY) {
      return;
    }

    const controller = new AbortController();
    checkForMissingBookings(controller.signal);
    return () => controller.abort();
  }, [calendarEvents, user, selectedType]);

  const checkForMissingBookings = async (signal?: AbortSignal) => {
    try {
      // Filter events that have venues and need checking (skip already-checked events)
      const eventsToCheck = calendarEvents.filter(event => {
        const venues = event.metadata?.venues || [];
        return venues.length > 0 &&
          event.missingVenues === undefined && // not yet checked
          (
            event.type === CalendarEventType.COACHING_SESSION ||
            event.type === CalendarEventType.COACHING_PROGRAM ||
            event.type === CalendarEventType.GROUP_PLAY
          );
      });

      if (eventsToCheck.length === 0) {
        return;
      }

      // Prepare instances data for API call
      const instances = eventsToCheck.map(event => {
        let instanceType: 'coachingSession' | 'groupPlayInstance' | 'coachingProgramInstance';
        
        if (event.type === CalendarEventType.COACHING_SESSION) {
          instanceType = 'coachingSession';
        } else if (event.type === CalendarEventType.GROUP_PLAY) {
          instanceType = 'groupPlayInstance';
        } else {
          instanceType = 'coachingProgramInstance';
        }

        return {
          id: event.id,
          type: instanceType,
          venues: event.metadata?.venues || [],
          sessionDate: event.date,
          // Use raw time values from metadata (not formatted)
          startTime: event.metadata?.startTime || convertTo24Hour(event.startTime),
          endTime: event.metadata?.endTime || convertTo24Hour(event.endTime)
        };
      });

      // Call API to check for missing bookings
      const response = await amenityAPI.checkMissingBookings(instances, signal);
      const results = response.data.results || [];

      // Update calendar events with missing booking information.
      // Always set missingVenues (even as []) so the event is marked as checked
      // and won't be re-fetched on the next render, breaking the feedback loop.
      const checkedIds = new Set(eventsToCheck.map(e => e.id));
      setCalendarEvents(prevEvents => {
        return prevEvents.map(event => {
          if (!checkedIds.has(event.id)) return event;
          const result = results.find((r: any) => r.instanceId === event.id);
          const missingVenues = result?.missingVenues ?? [];
          return {
            ...event,
            hasMissingBookings: missingVenues.length > 0,
            missingVenues
          };
        });
      });
    } catch (error: any) {
      if (error?.code === 'ERR_CANCELED') return; // Request was aborted on navigation
      console.error('Error checking for missing bookings:', error);
      // Silently fail - this is a non-critical feature
    }
  };

  const fetchAmenities = async () => {
    try {
      const response = await amenityAPI.getAmenities();
      setAvailableAmenities(response.data || []);
      // Auto-select first amenity if available and no saved preference
      if (response.data && response.data.length > 0 && !selectedAmenityId) {
        setSelectedAmenityId(response.data[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching amenities:', error);
    }
  };

  const fetchCalendarData = async () => {
    if (!selectedType) return;

    setLoading(true);
    setError(null);

    try {
      let events: CalendarEvent[] = [];

      const startDate = getDateRangeStart();
      const endDate = getDateRangeEnd();

      switch (selectedType) {
        case CalendarEventType.CLASS:
          events = await fetchClassData(startDate, endDate);
          break;
        case CalendarEventType.COACHING_SESSION:
          events = await fetchCoachingSessionData(startDate, endDate);
          break;
        case CalendarEventType.COACHING_PROGRAM:
          events = await fetchCoachingProgramData(startDate, endDate);
          break;
        case CalendarEventType.GROUP_PLAY:
          events = await fetchGroupPlayData(startDate, endDate);
          break;
        case CalendarEventType.AMENITY:
          events = await fetchAmenityData(startDate, endDate);
          break;
        case CalendarEventType.EVENT:
          events = await fetchEventData(startDate, endDate);
          break;
      }

      setCalendarEvents(events);
    } catch (error: any) {
      console.error('Error fetching calendar data:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch calendar data';
      setError(errorMessage);
      setCalendarEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    const events: CalendarEvent[] = [];

    // First, fetch class instances
    const instanceResponse = await classInstanceAPI.getInstances({
      startDate,
      endDate,
      includeBookings: true
    });

    // Track all instance dates (including cancelled) to prevent parent class from showing
    const instanceDateMap = new Map<string, Set<string>>(); // Map of parentClassId -> Set of dates

    (instanceResponse.data || []).forEach((instance: any) => {
      const classDate = isDateOnly(instance.sessionDate)
        ? formatDateForInput(parseDateOnlyString(instance.sessionDate))
        : instance.sessionDate.split('T')[0];

      // Track this instance date for its parent class (even if cancelled)
      if (instance.parentClassId) {
        if (!instanceDateMap.has(instance.parentClassId)) {
          instanceDateMap.set(instance.parentClassId, new Set());
        }
        instanceDateMap.get(instance.parentClassId)!.add(classDate);
      }

      // Skip cancelled instances from displaying in calendar
      if (instance.status?.toLowerCase() === 'cancelled') {
        console.log('Skipping cancelled instance on', instance.id);
        return;
      }

      const formattedStartTime = formatTimeString(instance.startTime);
      const formattedEndTime = formatTimeString(instance.endTime);

      const calendarEvent = {
        id: instance.id,
        title: instance.name,
        description: instance.description,
        startTime: formattedStartTime,
        endTime: formattedEndTime,
        date: classDate,
        type: CalendarEventType.CLASS,
        status: instance.status,
        capacity: instance.capacity,
        enrolledCount: instance.enrolledCount || 0,
        location: instance.venue,
        instructor: instance.instructor,
        cost: instance.costOverride || instance.parentClass?.oneTimeCost,
        classType: instance.type || instance.parentClass?.type, // Include class type for color coding
        metadata: {
          ...instance,
          isInstance: true,
          parentClassId: instance.parentClassId,
          parentClass: instance.parentClass
        }
      };
      
      events.push(calendarEvent);
    });

    // Then, fetch regular classes (for non-recurring classes only)
    const classResponse = await classAPI.getClasses({
      startDate,
      endDate
    });

    (classResponse.data || []).forEach((classItem: any) => {
      // Skip inactive classes
      if (classItem.active === false) {
        return;
      }

      // Skip recurring classes - they should have instances generated in the database
      if (classItem.recurringPattern) {
        return;
      }

      // For non-recurring classes, create a single event
      const classDate = isDateOnly(classItem.scheduleDate)
        ? formatDateForInput(parseDateOnlyString(classItem.scheduleDate))
        : classItem.scheduleDate.split('T')[0];

      // Check if the class date is within our date range
      if (classDate >= startDate && classDate <= endDate) {
        // Only show parent class if there's no instance for this specific date (including cancelled)
        const instanceDates = instanceDateMap.get(classItem.id);
        const hasInstanceOnDate = instanceDates && instanceDates.has(classDate);
        
        if (!hasInstanceOnDate) {
          events.push({
            id: classItem.id,
            title: classItem.name,
            description: classItem.description,
            startTime: formatTimeString(classItem.startTime),
            endTime: formatTimeString(classItem.endTime),
            date: classDate,
            type: CalendarEventType.CLASS,
            status: classItem.status || 'active',
            capacity: classItem.capacity,
            enrolledCount: classItem.currentBookings || 0,
            location: classItem.venue,
            instructor: classItem.instructorName || classItem.instructor,
            cost: classItem.oneTimeCost,
            classType: classItem.type, // Include class type for color coding
            metadata: {
              ...classItem,
              isInstance: false
            }
          });
        }
      }
    });

    return events;
  };

  // Helper function to generate recurring events for a class
  const generateRecurringClassEvents = (classItem: any, viewStartDate: string, viewEndDate: string): CalendarEvent[] => {
    const events: CalendarEvent[] = [];

    if (!classItem.recurringPattern || !classItem.scheduleDate) {
      return events;
    }

    const { frequency, daysOfWeek, interval, endDate } = classItem.recurringPattern;

    // Use proper date parsing to avoid timezone issues
    const classStart = isDateOnly(classItem.scheduleDate) ? parseDateOnlyString(classItem.scheduleDate) : new Date(classItem.scheduleDate);
    const classEnd = endDate ? (isDateOnly(endDate) ? parseDateOnlyString(endDate) : new Date(endDate)) : new Date('2099-12-31');
    const viewStart = parseDateOnlyString(viewStartDate);
    const viewEnd = parseDateOnlyString(viewEndDate);

    // Calculate the actual start and end dates for generation
    const actualStart = new Date(Math.max(classStart.getTime(), viewStart.getTime()));
    const actualEnd = new Date(Math.min(classEnd.getTime(), viewEnd.getTime()));

    if (actualStart > actualEnd) {
      return events; // No overlap between class dates and view dates
    }

    // Generate events based on frequency and days of week
    if (frequency === 'weekly' && daysOfWeek && daysOfWeek.length > 0) {
      // For weekly classes, generate events for each occurrence within the date range
      let currentDate = new Date(classStart);

      while (currentDate <= classEnd && currentDate <= viewEnd) {
        const dayOfWeek = currentDate.getDay();

        // Check if this day is in the daysOfWeek array
        if (daysOfWeek.includes(dayOfWeek)) {
          // Check if this date is within the view range
          if (currentDate >= viewStart && currentDate <= viewEnd) {
            const eventDate = formatDateForInput(currentDate);

            events.push({
              id: `${classItem.id}-${eventDate}`,
              title: classItem.name,
              description: classItem.description,
              startTime: classItem.startTime,
              endTime: classItem.endTime,
              date: eventDate,
              type: CalendarEventType.CLASS,
              status: classItem.status,
              capacity: classItem.capacity,
              enrolledCount: classItem.bookings?.length || 0,
              location: classItem.location,
              instructor: classItem.coach?.name || classItem.instructorName || classItem.instructor,
              cost: classItem.cost,
              classType: classItem.type, // Include class type for color coding
              metadata: {
                ...classItem,
                classType: classItem.classType,
                classLevel: classItem.classLevel,
                coach: classItem.coach,
                instructor: classItem.instructorName || classItem.instructor,
                sessionDate: eventDate
              }
            });
          }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (frequency === 'daily') {
      // For daily classes
      let currentDate = new Date(classStart);
      let dayCount = 0;

      while (currentDate <= classEnd && currentDate <= viewEnd) {
        // Check interval (every X days)
        if (dayCount % (interval || 1) === 0) {
          // Check if this date is within the view range
          if (currentDate >= viewStart && currentDate <= viewEnd) {
            const eventDate = formatDateForInput(currentDate);

            events.push({
              id: `${classItem.id}-${eventDate}`,
              title: classItem.name,
              description: classItem.description,
              startTime: classItem.startTime,
              endTime: classItem.endTime,
              date: eventDate,
              type: CalendarEventType.CLASS,
              status: classItem.status,
              capacity: classItem.capacity,
              enrolledCount: classItem.bookings?.length || 0,
              location: classItem.location,
              instructor: classItem.coach?.name || classItem.instructorName || classItem.instructor,
              cost: classItem.cost,
              classType: classItem.type, // Include class type for color coding
              metadata: {
                ...classItem,
                classType: classItem.classType,
                classLevel: classItem.classLevel,
                coach: classItem.coach,
                instructor: classItem.instructorName || classItem.instructor,
                sessionDate: eventDate
              }
            });
          }
        }

        dayCount++;
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // For other frequencies or fallback, create a single event
      if (viewStart <= classStart && classStart <= viewEnd) {
        const eventDate = formatDateForInput(classStart);

        events.push({
          id: classItem.id,
          title: classItem.name,
          description: classItem.description,
          startTime: classItem.startTime,
          endTime: classItem.endTime,
          date: eventDate,
          type: CalendarEventType.CLASS,
          status: classItem.status,
          capacity: classItem.capacity,
          enrolledCount: classItem.bookings?.length || 0,
          location: classItem.location,
          instructor: classItem.coach?.name || classItem.instructorName || classItem.instructor,
          cost: classItem.cost,
          classType: classItem.type, // Include class type for color coding
          metadata: {
            ...classItem,
            classType: classItem.classType,
            classLevel: classItem.classLevel,
            coach: classItem.coach,
            instructor: classItem.instructorName || classItem.instructor
          }
        });
      }
    }

    return events;
  };

  const fetchCoachingSessionData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    const response = await coachingSessionAPI.getCoachingSessions({
      startDate,
      endDate
    });

    return (response.data || []).map((session: any) => {
      // Use proper date parsing to avoid timezone issues
      const sessionDate = isDateOnly(session.sessionDate)
        ? formatDateForInput(parseDateOnlyString(session.sessionDate))
        : session.sessionDate.split('T')[0]; // For datetime strings, just take the date part

      return {
        id: session.id,
        title: session.name,
        description: session.description,
        startTime: session.startTime,
        endTime: session.endTime,
        date: sessionDate,
        type: CalendarEventType.COACHING_SESSION,
        status: session.status,
        capacity: session.maxParticipants,
        enrolledCount: session.enrollments?.length || 0,
        location: session.venue,
        instructor: session.coach?.name,
        cost: session.cost,
        metadata: {
          ...session,
          // Explicitly store raw time values and venues for booking checks
          startTime: session.startTime,
          endTime: session.endTime,
          venues: session.venues
        }
      };
    });
  };

  const fetchCoachingProgramData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    const response = await coachingProgramInstanceAPI.getInstances({
      startDate,
      endDate,
      includeEnrollments: true
    });

    const events: CalendarEvent[] = [];

    (response.data || []).forEach((instance: any) => {
      const sessionDate = isDateOnly(instance.sessionDate)
        ? formatDateForInput(parseDateOnlyString(instance.sessionDate))
        : instance.sessionDate.split('T')[0];

      events.push({
        id: instance.id,
        title: instance.name,
        description: instance.description,
        startTime: formatTimeString(instance.startTime),
        endTime: formatTimeString(instance.endTime),
        date: sessionDate,
        type: CalendarEventType.COACHING_PROGRAM,
        status: instance.status,
        capacity: instance.capacity,
        enrolledCount: instance.enrolledCount || 0,
        location: instance.venue,
        instructor: instance.coachName,
        cost: instance.dropInCostOverride || instance.parentProgram?.dropInRate,
        metadata: {
          ...instance,
          // Store raw time values for booking checks
          startTime: instance.startTime,
          endTime: instance.endTime,
          venues: instance.venues,
          isInstance: true,
          parentProgramId: instance.parentProgramId,
          parentProgram: instance.parentProgram
        }
      });
    });

    return events;
  };

  // Helper function to generate recurring events for a coaching program
  const generateRecurringEvents = (program: any, viewStartDate: string, viewEndDate: string): CalendarEvent[] => {
    const events: CalendarEvent[] = [];

    if (!program.recurringPattern || !program.startDate || !program.endDate) {
      // If no recurring pattern, create a single event
      const startDate = isDateOnly(program.startDate) ? parseDateOnlyString(program.startDate) : new Date(program.startDate);
      return [{
        id: program.id,
        title: program.name,
        description: program.description,
        startTime: program.recurringPattern?.startTime || program.sessionTime || '17:00',
        endTime: program.recurringPattern?.endTime || calculateEndTime(program.recurringPattern?.startTime || program.sessionTime || '17:00', program.sessionDurationMinutes || 60),
        date: formatDateForInput(startDate),
        type: CalendarEventType.COACHING_PROGRAM,
        status: program.status,
        capacity: program.capacity,
        enrolledCount: program.currentEnrollments || 0,
        location: program.venue,
        instructor: program.coachName,
        cost: program.totalCost,
        metadata: program
      }];
    }

    const { frequency, daysOfWeek, startTime, endTime } = program.recurringPattern;

    // Use proper date parsing to avoid timezone issues
    const programStart = isDateOnly(program.startDate) ? parseDateOnlyString(program.startDate) : new Date(program.startDate);
    const programEnd = isDateOnly(program.endDate) ? parseDateOnlyString(program.endDate) : new Date(program.endDate);
    const viewStart = parseDateOnlyString(viewStartDate);
    const viewEnd = parseDateOnlyString(viewEndDate);

    // Calculate the actual start and end dates for generation (intersection of program dates and view dates)
    const actualStart = new Date(Math.max(programStart.getTime(), viewStart.getTime()));
    const actualEnd = new Date(Math.min(programEnd.getTime(), viewEnd.getTime()));

    if (actualStart > actualEnd) {
      return events; // No overlap between program and view dates
    }

    // Generate events based on frequency and days of week
    if (frequency === 'weekly' && daysOfWeek && daysOfWeek.length > 0) {
      // For weekly programs, generate events for each occurrence within the date range
      let currentDate = new Date(programStart);

      while (currentDate <= programEnd) {
        const dayOfWeek = currentDate.getDay();

        // Check if this day is in the daysOfWeek array
        if (daysOfWeek.includes(dayOfWeek)) {
          // Check if this date is within the view range
          if (currentDate >= viewStart && currentDate <= viewEnd) {
            const eventDate = formatDateForInput(currentDate);
            const eventEndTime = endTime || calculateEndTime(startTime, program.sessionDurationMinutes || 60);

            events.push({
              id: `${program.id}-${eventDate}`,
              title: program.name,
              description: program.description,
              startTime: startTime,
              endTime: eventEndTime,
              date: eventDate,
              type: CalendarEventType.COACHING_PROGRAM,
              status: program.status,
              capacity: program.capacity,
              enrolledCount: program.currentEnrollments || 0,
              location: program.venue,
              instructor: program.coachName,
              cost: program.totalCost,
              metadata: { ...program, sessionDate: eventDate }
            });
          }
        }

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else if (frequency === 'biweekly' && daysOfWeek && daysOfWeek.length > 0) {
      // For biweekly programs
      let currentDate = new Date(programStart);
      let weekCount = 0;

      while (currentDate <= programEnd) {
        const dayOfWeek = currentDate.getDay();

        // Check if this day is in the daysOfWeek array and it's an appropriate week
        if (daysOfWeek.includes(dayOfWeek) && weekCount % 2 === 0) {
          // Check if this date is within the view range
          if (currentDate >= viewStart && currentDate <= viewEnd) {
            const eventDate = formatDateForInput(currentDate);
            const eventEndTime = endTime || calculateEndTime(startTime, program.sessionDurationMinutes || 60);

            events.push({
              id: `${program.id}-${eventDate}`,
              title: program.name,
              description: program.description,
              startTime: startTime,
              endTime: eventEndTime,
              date: eventDate,
              type: CalendarEventType.COACHING_PROGRAM,
              status: program.status,
              capacity: program.capacity,
              enrolledCount: program.currentEnrollments || 0,
              location: program.venue,
              instructor: program.coachName,
              cost: program.totalCost,
              metadata: { ...program, sessionDate: eventDate }
            });
          }
        }

        // Move to next day, and track weeks
        if (currentDate.getDay() === 6) { // Saturday, end of week
          weekCount++;
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // For other frequencies or fallback, create a single event
      if (viewStart <= programStart && programStart <= viewEnd) {
        const eventDate = formatDateForInput(programStart);
        const eventEndTime = endTime || calculateEndTime(startTime, program.sessionDurationMinutes || 60);

        events.push({
          id: program.id,
          title: program.name,
          description: program.description,
          startTime: startTime,
          endTime: eventEndTime,
          date: eventDate,
          type: CalendarEventType.COACHING_PROGRAM,
          status: program.status,
          capacity: program.capacity,
          enrolledCount: program.currentEnrollments || 0,
          location: program.venue,
          instructor: program.coachName,
          cost: program.totalCost,
          metadata: program
        });
      }
    }

    return events;
  };

  // Helper function to calculate end time from start time and duration
  const calculateEndTime = (startTime: string, durationMinutes: number = 60): string => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + durationMinutes);
    return date.toTimeString().split(' ')[0].slice(0, 5);
  };

  const fetchGroupPlayData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    const events: CalendarEvent[] = [];

    try {
      // First, get all group plays
      const groupPlaysResponse = await groupPlayAPI.getGroupPlays({
        startDate,
        endDate
      });

      const groupPlays = groupPlaysResponse.data || [];

      // For each group play, fetch its instances
      for (const groupPlay of groupPlays) {
        try {
          const instancesResponse = await groupPlayAPI.getGroupPlayInstances(groupPlay.id, {
            startDate,
            endDate
          });

          const instances = instancesResponse.data || [];

          instances.forEach((instance: any) => {
            // Skip cancelled instances from displaying in calendar
            if (instance.status?.toLowerCase() === 'cancelled') {
              return;
            }

            events.push({
              id: instance.id,
              title: instance.name,
              description: instance.description || groupPlay.description,
              startTime: formatTimeString(instance.startTime),
              endTime: formatTimeString(instance.endTime),
              date: formatDateForInput(parseDateOnlyString(instance.sessionDate)),
              type: CalendarEventType.GROUP_PLAY,
              status: instance.status,
              capacity: instance.capacity,
              enrolledCount: instance.confirmedCount || 0,
              location: instance.venues?.length > 0 ? `${instance.venues.length} venue(s)` : 'TBD',
              cost: instance.memberFeeOverride || groupPlay.memberSessionFee || 0,
              metadata: {
                ...instance,
                // Explicitly store raw time values and venues for booking checks
                startTime: instance.startTime,
                endTime: instance.endTime,
                venues: instance.venues,
                parentGroupPlayId: groupPlay.id,
                groupPlayType: groupPlay.type
              }
            });
          });
        } catch (instanceError) {
          console.error(`Error fetching instances for group play ${groupPlay.id}:`, instanceError);
          // Continue with next group play if one fails
        }
      }
    } catch (error: any) {
      console.error('Error fetching group play data:', error);
      throw error;
    }

    return events;
  };

  const fetchAmenityData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    if (!selectedAmenityId) return [];

    const events: CalendarEvent[] = [];

    // Fetch min booking duration from the dedicated endpoint
    try {
      const durationResponse = await amenityScheduleAPI.getMinBookingDuration(selectedAmenityId);
      const minDuration = durationResponse.data?.minBookingDurationMinutes || 30;
      setAmenitySlotDuration(minDuration);
    } catch (durationError) {
      console.error(`Error fetching min duration for amenity ${selectedAmenityId}:`, durationError);
      setAmenitySlotDuration(30); // Default to 30 minutes
    }

    // Iterate through each date in the range
    const start = parseDateOnlyString(startDate);
    const end = parseDateOnlyString(endDate);

    for (let currentDate = new Date(start); currentDate <= end; currentDate.setDate(currentDate.getDate() + 1)) {
      try {
        const dateString = formatDateForInput(currentDate);
        const response = await amenityScheduleAPI.getAvailableSlots(selectedAmenityId, dateString);

        const slots = response.data?.slots || response.data || [];
        if (!Array.isArray(slots)) continue;

        // Filter out unavailable and unscheduled slots
        const scheduledSlots = slots.filter((slot: any) =>
          slot.status && slot.status !== 'UNAVAILABLE' && slot.status !== 'OUT_OF_SCHEDULE'
        );

        scheduledSlots.forEach((slot: any, index: number) => {
          // Format status for display
          const formatStatus = (status: string) => {
            if (status.toUpperCase() === 'AVAILABLE' || status.toLowerCase() === 'available') {
              return 'Available';
            }

            
            
            if (status.toUpperCase() === 'ON_HOLD' || status.toLowerCase() === 'on_hold') {
              return 'On Hold';
            }
            // Capitalize first letter of each word
            return status.split('_').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            ).join(' ');
          };

          // Create title with booking count if multiple bookings
          let title = `${availableAmenities.find(a => a.id === selectedAmenityId)?.name || 'Amenity'} - ${formatStatus(slot.status)}`;
          if (slot.bookingCount && slot.bookingCount > 1) {
            title += ` (${slot.bookingCount} bookings)`;
          }

          // Create description showing all bookings
          let description = '';
          if (slot.allBookings && slot.allBookings.length > 0) {
            description = slot.allBookings.map((booking: any, idx: number) => 
              `${idx + 1}. ${booking.memberName}${booking.programName ? ` (${booking.programName})` : ''}${booking.coachingSessionName ? ` (${booking.coachingSessionName})` : ''}`
            ).join('\n');
          } else if (slot.bookedBy) {
            description = `Booked by: ${slot.bookedBy}`;
          }

          events.push({
            id: `${selectedAmenityId}-${dateString}-${slot.startTime}-${index}`,
            title: title,
            description: description,
            startTime: formatTimeString(slot.startTime),
            endTime: formatTimeString(slot.endTime),
            date: dateString, // Use the actual date instead of selectedDate
            type: CalendarEventType.AMENITY,
            status: slot.status,
            metadata: slot
          });
        });
      } catch (error) {
        console.error(`Error fetching amenity slots for ${formatDateForInput(currentDate)}:`, error);
        // Continue with next date if one fails
      }
    }

    return events;
  };

  const fetchEventData = async (startDate: string, endDate: string): Promise<CalendarEvent[]> => {
    const response = await eventAPI.getAll({
      includeRegistrations: true
    });

    // Filter events by date range
    const filteredEvents = response.data.filter((event: any) => {
      const eventDate = event.eventDateTime.split('T')[0];
      return eventDate >= startDate && eventDate <= endDate;
    });

    return filteredEvents.map((event: any) => {
      // Use proper date parsing to avoid timezone issues
      let eventDate: string;
      let startTime: string;

      if (event.eventDateTime) {
        if (isDateOnly(event.eventDateTime)) {
          // Date-only field
          eventDate = formatDateForInput(parseDateOnlyString(event.eventDateTime));
          startTime = '00:00'; // Default time for date-only events
        } else {
          // DateTime field - extract date and time properly
          const dateTime = new Date(event.eventDateTime);
          eventDate = formatDateForInput(dateTime);
          startTime = dateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        }
      } else {
        eventDate = '';
        startTime = '';
      }

      return {
        id: event.id,
        title: event.title,
        description: event.description,
        startTime: startTime,
        endTime: '', // Events typically don't have end times
        date: eventDate,
        type: CalendarEventType.EVENT,
        status: event.status,
        capacity: event.maxRegistrants,
        enrolledCount: event.registrations?.length || 0,
        location: event.location,
        cost: event.cost,
        metadata: event
      };
    });
  };

  const getDateRangeStart = (): string => {
    const date = new Date(selectedDate);

    switch (selectedView) {
      case CalendarViewType.DAILY:
        return formatDateForInput(date);
      case CalendarViewType.WEEKLY:
        // Start of week (Sunday)
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay());
        return formatDateForInput(startOfWeek);
      case CalendarViewType.MONTHLY:
        // Start of month
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        return formatDateForInput(startOfMonth);
      default:
        return formatDateForInput(date);
    }
  };

  const getDateRangeEnd = (): string => {
    const date = new Date(selectedDate);

    switch (selectedView) {
      case CalendarViewType.DAILY:
        return formatDateForInput(date);
      case CalendarViewType.WEEKLY:
        // End of week (Saturday)
        const endOfWeek = new Date(date);
        endOfWeek.setDate(date.getDate() + (6 - date.getDay()));
        return formatDateForInput(endOfWeek);
      case CalendarViewType.MONTHLY:
        // End of month
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return formatDateForInput(endOfMonth);
      default:
        return formatDateForInput(date);
    }
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(selectedDate);

    switch (selectedView) {
      case CalendarViewType.DAILY:
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case CalendarViewType.WEEKLY:
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case CalendarViewType.MONTHLY:
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }

    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date());
  };

  const openDatePickerDialog = () => {
    setTempSelectedDate(formatDateForInput(selectedDate));
    setOpenDatePicker(true);
  };

  const handleDatePickerConfirm = () => {
    if (tempSelectedDate) {
      const newDate = parseDateOnlyString(tempSelectedDate);
      setSelectedDate(newDate);
    }
    setOpenDatePicker(false);
  };

  const handleDatePickerCancel = () => {
    setOpenDatePicker(false);
    setTempSelectedDate('');
  };

  // Date helper functions
  const getDateRelativeToToday = (days: number): Date => {
    const date = new Date();
    date.setDate(date.getDate() + days);
    return date;
  };

  const getStartOfWeek = (): Date => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() - day;
    return new Date(date.setDate(diff));
  };

  const getEndOfWeek = (): Date => {
    const date = new Date();
    const day = date.getDay();
    const diff = date.getDate() + (6 - day);
    return new Date(date.setDate(diff));
  };

  const getStartOfMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getEndOfMonth = (): Date => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const isToday = (dateString: string): boolean => {
    const today = formatDateForInput(new Date());
    return dateString === today;
  };

  const scrollToCurrentTime = () => {
    if (!calendarContainerRef.current || selectedView !== CalendarViewType.DAILY) return;

    const now = new Date();
    const currentTimeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Try to find an element with the current time
    const timeElement = calendarContainerRef.current.querySelector(`[data-time="${currentTimeString.substring(0, 5)}"]`);
    if (timeElement) {
      const blockElement = timeElement.parentElement;
      if (blockElement) {
        const containerRect = calendarContainerRef.current.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        const scrollTop = blockRect.top - containerRect.top + calendarContainerRef.current.scrollTop;

        calendarContainerRef.current.scrollTo({
          top: Math.max(0, scrollTop - 100),
          behavior: 'smooth'
        });
      }
    }
  };

  const getEventStatusColor = (event: CalendarEvent) => {
    // For CLASS events, use class type colors
    if (event.type === CalendarEventType.CLASS && event.classType) {
      const classTypeColor = classTypeColorMap[event.classType.toLowerCase()];
      if (classTypeColor) {
        // Check for special states that should override the type color
        if (event.status?.toLowerCase() === 'cancelled') {
          // Darken the color for cancelled classes
          return '#999999';
        }
        
        // Check if event is in the past
        const now = new Date();
        const eventDate = new Date(event.date);
        let isPastEvent = false;
        
        if (event.startTime) {
          const [hours, minutes] = event.startTime.split(':').map(Number);
          const eventDateTime = new Date(eventDate);
          eventDateTime.setHours(hours, minutes, 0, 0);
          const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
          isPastEvent = eventDateTime < oneHourAgo;
        } else {
          isPastEvent = eventDate < new Date(now.getFullYear(), now.getMonth(), now.getDate());
        }
        
        if (isPastEvent) {
          // Desaturate the color for past classes
          return '#cccccc';
        }
        
        // Check if full
        const isFull = event.capacity && (event.enrolledCount || 0) >= event.capacity;
        if (isFull) {
          // Add a darker shade to indicate full
          return adjustColorBrightness(classTypeColor, -20);
        }
        
        return classTypeColor; // Use the class type color
      }
    }
    
    if (event.type === CalendarEventType.AMENITY) {
      const status = event.status?.toUpperCase(); // Handle case variations
      switch (status) {
        case 'AVAILABLE': return '#c8e6c9'; // Light green
        case 'BOOKED': return '#ffcdd2'; // Light red/pink
        case 'ON_HOLD': return '#ffe0b2'; // Light orange
        case 'UNAVAILABLE': return '#f5f5f5'; // Light gray
        default:
          return '#f5f5f5';
      }
    }

    // Check if event is in the past
    const now = new Date();
    const eventDate = new Date(event.date);

    // For events with start time, check if the event has already ended
    let isPastEvent = false;
    if (event.startTime) {
      const [hours, minutes] = event.startTime.split(':').map(Number);
      const eventDateTime = new Date(eventDate);
      eventDateTime.setHours(hours, minutes, 0, 0);

      // Consider event as past if it started more than 1 hour ago (to account for ongoing classes)
      const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
      isPastEvent = eventDateTime < oneHourAgo;
    } else {
      // For events without time, just check the date
      isPastEvent = eventDate < new Date(now.getFullYear(), now.getMonth(), now.getDate());
    }

    // Check if event is full (for classes and other events with capacity)
    const isFull = event.capacity && (event.enrolledCount || 0) >= event.capacity;

    // Handle cancelled status first
    if (event.status?.toLowerCase() === 'cancelled') {
      return '#f44336'; // Red
    }

    // Handle full capacity
    if (isFull) {
      return '#ff9800'; // Orange
    }

    // Handle past events
    if (isPastEvent) {
      return '#9e9e9e'; // Gray
    }

    // Handle other statuses
    switch (event.status?.toLowerCase()) {
      case 'active':
      case 'scheduled':
        return '#4caf50'; // Green
      case 'draft':
        return '#9e9e9e'; // Gray
      default:
        return '#4caf50'; // Green - default for active classes
    }
  };

  // Helper function to adjust color brightness
  const adjustColorBrightness = (hex: string, percent: number): string => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB values
    let r = parseInt(hex.substring(0, 2), 16);
    let g = parseInt(hex.substring(2, 4), 16);
    let b = parseInt(hex.substring(4, 6), 16);
    
    // Adjust brightness
    r = Math.max(0, Math.min(255, r + (r * percent / 100)));
    g = Math.max(0, Math.min(255, g + (g * percent / 100)));
    b = Math.max(0, Math.min(255, b + (b * percent / 100)));
    
    // Convert back to hex
    const rr = Math.round(r).toString(16).padStart(2, '0');
    const gg = Math.round(g).toString(16).padStart(2, '0');
    const bb = Math.round(b).toString(16).padStart(2, '0');
    
    return `#${rr}${gg}${bb}`;
  };

  const getEventTextColor = (event: CalendarEvent) => {
    // Get the background color for this event
    const bgColor = getEventStatusColor(event);
    
    // Calculate luminance to determine if we need light or dark text
    const getLuminance = (hex: string): number => {
      hex = hex.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;
      
      // Apply gamma correction
      const rsRGB = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
      const gsRGB = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
      const bsRGB = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);
      
      return 0.2126 * rsRGB + 0.7152 * gsRGB + 0.0722 * bsRGB;
    };
    
    const luminance = getLuminance(bgColor);
    
    // Use dark text for light backgrounds (luminance > 0.5), light text for dark backgrounds
    return luminance > 0.5 ? '#333333' : '#ffffff';
  };

  const getEventTypeIcon = (type: CalendarEventType) => {
    switch (type) {
      case CalendarEventType.CLASS: return <ClassIcon />;
      case CalendarEventType.COACHING_SESSION: return <CoachingIcon />;
      case CalendarEventType.COACHING_PROGRAM: return <CoachingIcon />;
      case CalendarEventType.GROUP_PLAY: return <CoachingIcon />;
      case CalendarEventType.AMENITY: return <AmenityIcon />;
      case CalendarEventType.EVENT: return <EventIcon />;
      default: return <CoachingIcon />;
    }
  };

  const getPricingTierIcon = (event: CalendarEvent) => {
    if (event.type !== CalendarEventType.AMENITY || !event.metadata?.pricingTier) {
      return null;
    }

    const pricingTier = event.metadata.pricingTier.toLowerCase();
    
    switch (pricingTier) {
      case 'peak':
        return <AttachMoneyIcon sx={{ fontSize: 16, ml: 0.5 }} />;
      case 'off-peak':
        return <MoneyOffIcon sx={{ fontSize: 16, ml: 0.5 }} />;
      case 'normal':
      default:
        return null;
    }
  };

  const getTooltipContent = (event: CalendarEvent) => {
    // Helper to render missing bookings warning
    const renderMissingBookingsWarning = () => {
      if (!event.hasMissingBookings || !event.missingVenues || event.missingVenues.length === 0) {
        return null;
      }

      const missingCount = event.missingVenues.length;
      const totalVenues = event.metadata?.venues?.length || 0;

      return (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255, 255, 255, 0.2)' }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningIcon sx={{ fontSize: '1rem' }} />
            Missing Court Bookings
          </Typography>
          <Typography variant="body2">
            {missingCount} of {totalVenues} court{totalVenues > 1 ? 's' : ''} missing booking{missingCount > 1 ? 's' : ''}
          </Typography>
        </Box>
      );
    };

    if (event.type === CalendarEventType.CLASS) {
      const classData = event.metadata;
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {event.title}
          </Typography>
          {(classData?.classType?.name || classData?.type) && (
            <Typography variant="body2">
              Type: {classData?.classType?.name || classData?.type || 'N/A'}
            </Typography>
          )}
          {(classData?.classLevel?.name || classData?.level) && (
            <Typography variant="body2">
              Level: {classData?.classLevel?.name || classData?.level || 'N/A'}
            </Typography>
          )}
          <Typography variant="body2">
            Enrolled: {event.enrolledCount || 0}{event.capacity ? `/${event.capacity}` : ''}
          </Typography>
          {(event.instructor || classData?.instructor || classData?.coach?.name) && (
            <Typography variant="body2">
              Instructor: {event.instructor || classData?.instructor || classData?.coach?.name}
            </Typography>
          )}
          {(event.location || classData?.location) && (
            <Typography variant="body2">
              Location: {event.location || classData?.location}
            </Typography>
          )}
          <Typography variant="body2">
            Time: {event.startTime} - {event.endTime}
          </Typography>
          {classData?.status && (
            <Typography variant="body2">
              Status: {classData.status}
            </Typography>
          )}
          {renderMissingBookingsWarning()}
        </Box>
      );
    }

    // Enhanced tooltip for amenity with multiple bookings
    if (event.type === CalendarEventType.AMENITY && event.metadata?.allBookings && event.metadata.allBookings.length > 1) {
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {event.title}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Time: {event.startTime} - {event.endTime}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1, mb: 0.5 }}>
            Bookings ({event.metadata.bookingCount}):
          </Typography>
          {event.metadata.allBookings.map((booking: any, idx: number) => (
            <Box key={booking.id} sx={{ ml: 1, mb: 0.5 }}>
              <Typography variant="body2">
                {idx + 1}. {booking.memberName}
              </Typography>
              {booking.programName && (
                <Typography variant="caption" sx={{ ml: 2, display: 'block' }}>
                  Program: {booking.programName}
                </Typography>
              )}
              {booking.coachingSessionName && (
                <Typography variant="caption" sx={{ ml: 2, display: 'block' }}>
                  Session: {booking.coachingSessionName}
                </Typography>
              )}
              <Typography variant="caption" sx={{ ml: 2, display: 'block', color: 'text.secondary' }}>
                {booking.startTime} - {booking.endTime}
              </Typography>
            </Box>
          ))}
          {event.metadata?.pricingTier && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Pricing: {event.metadata.pricingTier.charAt(0).toUpperCase() + event.metadata.pricingTier.slice(1).replace('-', ' ')}
            </Typography>
          )}
          {renderMissingBookingsWarning()}
        </Box>
      );
    }

    // Default tooltip for other event types
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          {event.title}
        </Typography>
        {event.description && (
          <Typography variant="body2">
            {event.description}
          </Typography>
        )}
        {event.instructor && (
          <Typography variant="body2">
            Instructor: {event.instructor}
          </Typography>
        )}
        {event.location && (
          <Typography variant="body2">
            Location: {event.location}
          </Typography>
        )}
        <Typography variant="body2">
          Time: {event.startTime} - {event.endTime}
        </Typography>
        {event.type === CalendarEventType.AMENITY && event.metadata?.pricingTier && (
          <Typography variant="body2">
            Pricing: {event.metadata.pricingTier.charAt(0).toUpperCase() + event.metadata.pricingTier.slice(1).replace('-', ' ')}
          </Typography>
        )}
        {renderMissingBookingsWarning()}
      </Box>
    );
  };

  const renderDailyView = () => {
    const timeBlocks = generateTimeBlocks();
    // Calculate block interval the same way as generateTimeBlocks
    let blockInterval = 30;
    if (selectedType === CalendarEventType.AMENITY && amenitySlotDuration > 0) {
      blockInterval = gcd(30, amenitySlotDuration);
    }
    const blockHeight = 60; // Base height per block in pixels

    // Get all events for the selected date and calculate their layout
    const dayEvents = calendarEvents.filter(event => event.date === formatDateForInput(selectedDate));
    const eventLayout = calculateEventLayout(dayEvents);

    // Create a set of time block indices that have events or are spanned by events
    const occupiedSlots = new Set<number>();
    
    if (dayEvents.length > 0) {
      timeBlocks.forEach((block, i) => {
        if (block.events.length > 0) {
          // Mark this slot and all slots it spans
          block.events.forEach(event => {
            const eventSpan = getEventSlotSpan(event, blockInterval);
            for (let j = 0; j < eventSpan; j++) {
              occupiedSlots.add(i + j);
            }
          });
        }
      });
    }

    return (
      <Box
        ref={calendarContainerRef}
        sx={{
          overflow: 'hidden',
          maxHeight: '70vh',
          overflowY: 'auto'
        }}
      >
        {dayEvents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              No events
            </Typography>
          </Box>
        ) : (
          timeBlocks
            .filter((block, index) => {
              // Only show slots that are occupied by events
              return occupiedSlots.has(index);
            })
            .map((block, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              borderBottom: 1,
              borderColor: 'divider',
              minHeight: blockHeight,
              '&:hover': { bgcolor: 'grey.50' }
            }}
          >
            <Box
              sx={{
                width: { xs: 60, md: 80 },
                p: { xs: 0.5, md: 1 },
                borderRight: 1,
                borderColor: 'divider',
                bgcolor: 'grey.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 'bold', fontSize: { xs: '0.65rem', md: '0.75rem' } }}
                data-time={block.time}
              >
                {block.displayTime}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, p: 0.5, position: 'relative' }}>
              {block.events.map((event, eventIndex) => {
                const eventSpan = getEventSlotSpan(event, blockInterval);
                const eventHeight = eventSpan * blockHeight;
                
                // Get layout information for this event
                const layout = eventLayout.get(event.id) || { column: 0, totalColumns: 1 };
                const { column, totalColumns } = layout;
                
                // Calculate horizontal position based on layout
                const eventWidth = totalColumns > 1 ? `calc(${100 / totalColumns}% - 8px)` : 'calc(100% - 8px)';
                const leftOffset = totalColumns > 1 ? `calc(${(100 / totalColumns) * column}% + 4px)` : '4px';
                
                return (
                  <Tooltip
                    key={eventIndex}
                    title={getTooltipContent(event)}
                    arrow
                    placement="right"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          maxWidth: 300,
                          bgcolor: 'rgba(0, 0, 0, 0.9)',
                          color: 'white',
                          fontSize: '0.875rem',
                          padding: 2
                        }
                      }
                    }}
                  >
                    <Card
                      sx={{
                        position: 'absolute',
                        top: 4,
                        left: leftOffset,
                        width: eventWidth,
                        height: `${eventHeight - 8}px`,
                        bgcolor: getEventStatusColor(event),
                        color: getEventTextColor(event),
                        cursor: 'pointer',
                        zIndex: 1,
                        '&:hover': {
                          boxShadow: 3,
                          transform: 'translateY(-1px)',
                          transition: 'all 0.2s ease-in-out',
                          zIndex: 2
                        }
                      }}
                      onClick={() => handleEventClick(event)}
                    >
                      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          {getEventTypeIcon(event.type)}
                          <Typography variant="subtitle2" sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.8rem', flex: 1 }}>
                            {event.type === CalendarEventType.GROUP_PLAY && event.metadata?.groupPlayType
                              ? `[${event.metadata.groupPlayType.charAt(0).toUpperCase() + event.metadata.groupPlayType.slice(1)}] ${event.title}`
                              : event.title}
                          </Typography>
                          {event.hasMissingBookings && (user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.EMPLOYEE || user?.role === UserRole.COACH) && (
                            <Tooltip title={`Missing ${event.missingVenues?.length} court booking${(event.missingVenues?.length || 0) > 1 ? 's' : ''}`}>
                              <WarningIcon sx={{ fontSize: '1rem', color: '#ff9800', ml: 0.5 }} />
                            </Tooltip>
                          )}
                          {getPricingTierIcon(event)}
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                          {event.startTime} - {event.endTime}
                        </Typography>
                        {event.instructor && (
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            Instructor: {event.instructor}
                          </Typography>
                        )}
                        {event.capacity && (
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            {event.enrolledCount}/{event.capacity} enrolled
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        ))
        )}
      </Box>
    );
  };

  const renderWeeklyView = () => {
    const weekDays = getWeekDays();

    return (
      <Box sx={{ overflow: 'hidden' }}>
        {/* Week header */}
        <Box sx={{ display: 'flex', bgcolor: 'grey.100' }}>
          <Box sx={{
            width: 80,
            p: 1,
            borderRight: 1,
            borderColor: 'divider',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }} />
          {weekDays.map((day, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                p: 1,
                textAlign: 'center',
                borderRight: index < weekDays.length - 1 ? 1 : 0,
                borderColor: 'divider',
                bgcolor: isToday(day.fullDate) ? 'primary.light' : 'grey.100',
                color: isToday(day.fullDate) ? 'primary.contrastText' : 'inherit',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: isToday(day.fullDate) ? 'primary.main' : 'grey.200',
                  transition: 'background-color 0.2s'
                }
              }}
              onClick={() => handleDateClick(day.fullDate)}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {day.dayName}
              </Typography>
              <Typography variant="h6">
                {day.date}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Week content */}
        <Box
          ref={calendarContainerRef}
          sx={{ maxHeight: '60vh', overflowY: 'auto' }}
        >
          {generateTimeBlocks().map((block, blockIndex) => (
            <Box
              key={blockIndex}
              sx={{
                display: 'flex',
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 60
              }}
            >
              <Box
                sx={{
                  width: 80,
                  p: 1,
                  borderRight: 1,
                  borderColor: 'divider',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                  {block.displayTime}
                </Typography>
              </Box>
              {weekDays.map((day, dayIndex) => (
                <Box
                  key={dayIndex}
                  sx={{
                    flex: 1,
                    p: 1,
                    borderRight: dayIndex < weekDays.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                    bgcolor: isToday(day.fullDate) ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                  }}
                >
                  {getEventsForDay(day.fullDate)
                    .filter(event => isEventInTimeSlot(event, block.time))
                    .map((event, eventIndex) => (
                      <Tooltip
                        key={eventIndex}
                        title={getTooltipContent(event)}
                        arrow
                        placement="top"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 300,
                              bgcolor: 'rgba(0, 0, 0, 0.9)',
                              color: 'white',
                              fontSize: '0.875rem',
                              padding: 2
                            }
                          }
                        }}
                      >
                        <Card
                          sx={{
                            mb: 0.5,
                            bgcolor: getEventStatusColor(event),
                            color: getEventTextColor(event),
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: 2,
                              transform: 'translateY(-1px)',
                              transition: 'all 0.2s ease-in-out'
                            }
                          }}
                          onClick={() => handleEventClick(event)}
                        >
                          <CardContent sx={{ p: 0.5, '&:last-child': { pb: 0.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', flex: 1 }}>
                                {event.type === CalendarEventType.GROUP_PLAY && event.metadata?.groupPlayType
                                  ? `${event.title} (${event.metadata.groupPlayType.charAt(0).toUpperCase() + event.metadata.groupPlayType.slice(1)})`
                                  : event.title}
                              </Typography>
                              {event.hasMissingBookings && (user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.EMPLOYEE || user?.role === UserRole.COACH) && (
                                <Tooltip title={`Missing ${event.missingVenues?.length} court booking${(event.missingVenues?.length || 0) > 1 ? 's' : ''}`}>
                                  <WarningIcon sx={{ fontSize: '0.9rem', color: '#ff9800', ml: 0.5 }} />
                                </Tooltip>
                              )}
                              {getPricingTierIcon(event)}
                            </Box>
                          </CardContent>
                        </Card>
                      </Tooltip>
                    ))}
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderMonthlyView = () => {
    const monthDays = getMonthDays();
    const weeks = [];

    // Group days into weeks
    for (let i = 0; i < monthDays.length; i += 7) {
      weeks.push(monthDays.slice(i, i + 7));
    }

    return (
      <Box sx={{ overflow: 'hidden' }}>
        {/* Month header */}
        <Box sx={{ display: 'flex', bgcolor: 'grey.100' }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((dayName, index) => (
            <Box
              key={index}
              sx={{
                flex: 1,
                p: 2,
                textAlign: 'center',
                borderRight: index < 6 ? 1 : 0,
                borderColor: 'divider'
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {dayName}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Month content */}
        {weeks.map((week, weekIndex) => (
          <Box key={weekIndex} sx={{ display: 'flex' }}>
            {week.map((day, dayIndex) => (
              <Box
                key={dayIndex}
                sx={{
                  flex: 1,
                  minHeight: 120,
                  p: 1,
                  borderRight: dayIndex < 6 ? 1 : 0,
                  borderBottom: weekIndex < weeks.length - 1 ? 1 : 0,
                  borderColor: 'divider',
                  bgcolor: day.isToday
                    ? 'primary.light'
                    : day.isCurrentMonth
                      ? 'white'
                      : 'grey.50',
                  position: 'relative',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: day.isToday
                      ? 'primary.main'
                      : day.isCurrentMonth
                        ? 'grey.100'
                        : 'grey.200',
                    transition: 'background-color 0.2s'
                  }
                }}
                onClick={(e) => {
                  // Only navigate if not clicking on an event chip
                  if (!(e.target as HTMLElement).closest('.MuiChip-root')) {
                    handleDateClick(day.fullDate);
                  }
                }}
              >
                {day.isToday && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                      pointerEvents: 'none'
                    }}
                  />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: day.isToday ? 'bold' : 'normal',
                    color: day.isToday
                      ? 'primary.contrastText'
                      : day.isCurrentMonth
                        ? 'text.primary'
                        : 'text.secondary',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  {day.date}
                </Typography>
                {getEventsForDay(day.fullDate).slice(0, 3).map((event, eventIndex) => {
                  // Helper function to truncate title
                  const truncateTitle = (title: string, maxLength: number = 20) => {
                    return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
                  };
                  
                  return (
                    <Tooltip
                      key={eventIndex}
                      title={getTooltipContent(event)}
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 300,
                            bgcolor: 'rgba(0, 0, 0, 0.9)',
                            color: 'white',
                            fontSize: '0.875rem',
                            padding: 2
                          }
                        }
                      }}
                    >
                      <Chip
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                            {event.startTime && (
                              <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {event.startTime}
                              </span>
                            )}
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {event.type === CalendarEventType.GROUP_PLAY && event.metadata?.groupPlayType
                                ? truncateTitle(`${event.title} (${event.metadata.groupPlayType.charAt(0).toUpperCase() + event.metadata.groupPlayType.slice(1)})`, 18)
                                : truncateTitle(event.title, 18)}
                            </span>
                            {event.hasMissingBookings && (user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.EMPLOYEE || user?.role === UserRole.COACH) && (
                              <Tooltip title={`Missing ${event.missingVenues?.length} court booking${(event.missingVenues?.length || 0) > 1 ? 's' : ''}`}>
                                <WarningIcon sx={{ fontSize: '0.7rem', color: '#ff9800' }} />
                              </Tooltip>
                            )}
                            {getPricingTierIcon(event)}
                          </Box>
                        }
                        size="small"
                        sx={{
                          display: 'block',
                          mb: 0.3,
                          ml: 0.5,
                          bgcolor: getEventStatusColor(event),
                          color: getEventTextColor(event),
                          fontSize: '0.6rem',
                          height: '18px',
                          cursor: 'pointer',
                          position: 'relative',
                          zIndex: 2,
                          '&:hover': {
                            boxShadow: 1,
                            transform: 'translateY(-1px)',
                            transition: 'all 0.2s ease-in-out'
                          },
                          '& .MuiChip-label': {
                            color: getEventTextColor(event),
                            padding: '0 6px',
                            display: 'block',
                            overflow: 'hidden'
                          }
                        }}
                        onClick={() => handleEventClick(event)}
                      />
                    </Tooltip>
                  );
                })}
                {getEventsForDay(day.fullDate).length > 3 && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ position: 'relative', zIndex: 2 }}
                  >
                    +{getEventsForDay(day.fullDate).length - 3} more
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  };

  // Helper function to convert 12-hour formatted time back to 24-hour format
  const convertTo24Hour = (time12h: string): string => {
    if (!time12h) return '';
    
    try {
      // Check if already in 24-hour format (HH:MM or HH:MM:SS)
      if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(time12h.trim())) {
        // Already in 24-hour format, just ensure padding
        const [hours, minutes] = time12h.split(':');
        return `${hours.padStart(2, '0')}:${minutes}`;
      }
      
      // Parse times like "05:00 PM", "5:00 AM", etc.
      const trimmed = time12h.trim();
      const parts = trimmed.split(' ');
      
      if (parts.length < 2) {
        // No meridiem, assume already 24-hour format
        const [hours, minutes] = trimmed.split(':');
        return `${hours.padStart(2, '0')}:${minutes || '00'}`;
      }
      
      const [timePart, meridiem] = parts;
      const [hours, minutes] = timePart.split(':');
      
      let hour24 = parseInt(hours, 10);
      
      if (meridiem?.toUpperCase() === 'PM' && hour24 !== 12) {
        hour24 += 12;
      } else if (meridiem?.toUpperCase() === 'AM' && hour24 === 12) {
        hour24 = 0;
      }
      
      return `${hour24.toString().padStart(2, '0')}:${minutes || '00'}`;
    } catch (error) {
      console.error('Error converting time to 24h format:', time12h, error);
      return '';
    }
  };

  // Helper function to convert time string to minutes since midnight
  const timeToMinutes = (timeString: string): number => {
    const time24 = convertTo24Hour(timeString);
    if (!time24) return 0;
    const [hours, minutes] = time24.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Check if two events overlap in time
  const eventsOverlap = (event1: CalendarEvent, event2: CalendarEvent): boolean => {
    const start1 = timeToMinutes(event1.startTime);
    const end1 = timeToMinutes(event1.endTime);
    const start2 = timeToMinutes(event2.startTime);
    const end2 = timeToMinutes(event2.endTime);
    
    // Events overlap if one starts before the other ends
    return start1 < end2 && start2 < end1;
  };

  // Calculate layout positions for overlapping events
  const calculateEventLayout = (events: CalendarEvent[]): Map<string, { column: number; totalColumns: number }> => {
    const layout = new Map<string, { column: number; totalColumns: number }>();
    
    if (events.length === 0) return layout;
    
    // Sort events by start time, then by duration (longer events first)
    const sortedEvents = [...events].sort((a, b) => {
      const startDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
      if (startDiff !== 0) return startDiff;
      
      const durationA = timeToMinutes(a.endTime) - timeToMinutes(a.startTime);
      const durationB = timeToMinutes(b.endTime) - timeToMinutes(b.startTime);
      return durationB - durationA; // Longer events first
    });
    
    // Group overlapping events
    const groups: CalendarEvent[][] = [];
    
    sortedEvents.forEach(event => {
      // Find a group this event overlaps with
      let addedToGroup = false;
      
      for (const group of groups) {
        if (group.some(e => eventsOverlap(e, event))) {
          group.push(event);
          addedToGroup = true;
          break;
        }
      }
      
      if (!addedToGroup) {
        groups.push([event]);
      }
    });
    
    // Assign columns within each group
    groups.forEach(group => {
      const columns: CalendarEvent[][] = [];
      
      group.forEach(event => {
        // Find first available column
        let columnIndex = 0;
        let placed = false;
        
        while (!placed) {
          if (!columns[columnIndex]) {
            columns[columnIndex] = [];
          }
          
          // Check if this event overlaps with any event in this column
          const overlapsInColumn = columns[columnIndex].some(e => eventsOverlap(e, event));
          
          if (!overlapsInColumn) {
            columns[columnIndex].push(event);
            layout.set(event.id, { column: columnIndex, totalColumns: 0 }); // Will update totalColumns later
            placed = true;
          } else {
            columnIndex++;
          }
        }
      });
      
      // Update totalColumns for all events in this group
      const totalColumns = columns.length;
      group.forEach(event => {
        const current = layout.get(event.id);
        if (current) {
          layout.set(event.id, { ...current, totalColumns });
        }
      });
    });
    
    return layout;
  };

  const generateTimeBlocks = () => {
    const blocks = [];
    
    // For amenity view, calculate the appropriate block interval
    // Use GCD of 30 and amenitySlotDuration to handle common cases:
    // - 30 min slots: GCD(30, 30) = 30 (blocks every 30 min)
    // - 45 min slots: GCD(30, 45) = 15 (blocks every 15 min to accommodate both)
    // - 60 min slots: GCD(30, 60) = 30 (blocks every 30 min)
    // For other views, use 30-minute intervals
    let blockInterval = 30;
    if (selectedType === CalendarEventType.AMENITY && amenitySlotDuration > 0) {
      blockInterval = gcd(30, amenitySlotDuration);
    }
    
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += blockInterval) {
        const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });

        const events = selectedView === CalendarViewType.DAILY
          ? getEventsForTimeSlot(time, blockInterval)
          : [];

        blocks.push({ time, displayTime, events });
      }
    }
    return blocks;
  };

  const getEventsForTimeSlot = (timeSlot: string, blockInterval: number = 30) => {
    const eventsForSelectedDate = calendarEvents.filter(event => event.date === formatDateForInput(selectedDate));
    const matchingEvents = eventsForSelectedDate.filter(event => {
      if (!event.startTime) return false;
      
      // Convert the formatted time back to 24-hour format for comparison
      const eventTime24 = convertTo24Hour(event.startTime);
      if (!eventTime24) return false;
      
      // Parse both times to minutes for comparison
      const [eventHour, eventMinute] = eventTime24.split(':').map(Number);
      const [slotHour, slotMinute] = timeSlot.split(':').map(Number);
      
      const eventTotalMinutes = eventHour * 60 + eventMinute;
      const slotTotalMinutes = slotHour * 60 + slotMinute;
      
      // Event belongs to this time slot if it starts within the block interval window
      // This matches the logic used in weekly view for consistency
      return eventTotalMinutes >= slotTotalMinutes && eventTotalMinutes < slotTotalMinutes + blockInterval;
    });
    
    return matchingEvents;
  };

  const getEventsForDay = (date: string) => {
    return calendarEvents.filter(event => event.date === date);
  };

  const getEventSlotSpan = (event: CalendarEvent, blockInterval: number): number => {
    // Calculate how many time blocks this event spans
    const startMinutes = timeToMinutes(event.startTime);
    const endMinutes = timeToMinutes(event.endTime);
    const durationMinutes = endMinutes - startMinutes;
    
    return Math.ceil(durationMinutes / blockInterval);
  };

  const isEventInTimeSlot = (event: CalendarEvent, timeSlot: string, blockInterval: number = 30) => {
    if (!event.startTime) return false;
    
    // Convert the formatted time back to 24-hour format for comparison
    const eventTime24 = convertTo24Hour(event.startTime);
    if (!eventTime24) return false;
    
    // Parse both times to minutes for comparison
    const [eventHour, eventMinute] = eventTime24.split(':').map(Number);
    const [slotHour, slotMinute] = timeSlot.split(':').map(Number);
    
    const eventTotalMinutes = eventHour * 60 + eventMinute;
    const slotTotalMinutes = slotHour * 60 + slotMinute;
    
    // Event belongs to this time slot if it starts within the block interval window
    return eventTotalMinutes >= slotTotalMinutes && eventTotalMinutes < slotTotalMinutes + blockInterval;
  };

  const getWeekDays = () => {
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      days.push({
        dayName: day.toLocaleDateString('en-US', { weekday: 'short' }),
        date: day.getDate(),
        fullDate: formatDateForInput(day)
      });
    }
    return days;
  };

  const getMonthDays = () => {
    const firstDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
    const lastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    const days = [];
    const today = new Date();

    for (let i = 0; i < 42; i++) { // 6 weeks * 7 days
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);

      days.push({
        date: day.getDate(),
        fullDate: formatDateForInput(day),
        isCurrentMonth: day.getMonth() === selectedDate.getMonth(),
        isToday: day.toDateString() === today.toDateString()
      });
    }
    return days;
  };

  // Handle date click in monthly/weekly view - navigate to daily view
  const handleDateClick = (dateString: string) => {
    const clickedDate = parseDateOnlyString(dateString);
    setSelectedDate(clickedDate);
    setSelectedView(CalendarViewType.DAILY);
  };

  const handleEventClick = (event: CalendarEvent) => {

    // For recurring events, the ID might have a date suffix (e.g., "classId-YYYY-MM-DD")
    // We need to strip that part to get the original ID
    const datePattern = /-\d{4}-\d{2}-\d{2}$/;

    // Navigate to appropriate detail page based on event type
    switch (event.type) {
      case CalendarEventType.CLASS:
        // Check if this is a class instance or a regular (non-recurring) class
        if (event.metadata?.isInstance) {
          // For members, redirect to the booking page instead of the instance detail page
          if (user?.role === UserRole.MEMBER) {
            navigate(`/member/class-booking/${event.id}`, { state: { from: 'calendar' } });
          } else {
            // This is a class instance, navigate to the instance detail page for admin/staff
            navigate(`/class-instances/${event.id}`);
          }
        } else {
          // This is a non-recurring class, navigate to class detail page
          navigate(`/classes/${event.id}`, { state: { from: 'calendar' } });
        }
        break;
      case CalendarEventType.COACHING_SESSION:
        navigate(`/coaching/sessions/${event.id}`);
        break;
      case CalendarEventType.COACHING_PROGRAM:
        // Check if this is a coaching program instance or a regular coaching program
        if (event.metadata?.isInstance && event.metadata?.parentProgramId) {
          // This is a coaching program instance, navigate to the parent program detail page
          navigate(`/coaching/programs/${event.metadata.parentProgramId}`);
        } else {
          // This is a regular coaching program, use the event ID (strip date suffix if present)
          const coachingProgramId = datePattern.test(event.id) ? event.id.replace(datePattern, '') : event.id;
          navigate(`/coaching/programs/${coachingProgramId}`);
        }
        break;
      case CalendarEventType.AMENITY:
        // Check if the amenity slot is booked or available
        if (event.metadata?.bookingId) {
          // Slot is booked, navigate to booking detail page
          navigate(`/amenities/${selectedAmenityId}/bookings/${event.metadata.bookingId}`);
        } else if (event.status?.toLowerCase() === 'available') {
          // Slot is available, open booking dialog
          const amenityToBook = availableAmenities.find(a => a.id === selectedAmenityId);
          if (amenityToBook) {
            setBookingDialogAmenity(amenityToBook);
            setBookingDialogDate(parseDateOnlyString(event.date));
            // Convert formatted time back to HH:MM:SS format for the dialog
            const convertTo24HourFormat = (time12h: string): string => {
              const match = time12h.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
              if (!match) return time12h;
              let [, hours, minutes, period] = match;
              let hour = parseInt(hours);
              if (period.toUpperCase() === 'PM' && hour !== 12) hour += 12;
              if (period.toUpperCase() === 'AM' && hour === 12) hour = 0;
              return `${hour.toString().padStart(2, '0')}:${minutes}:00`;
            };
            setBookingDialogSlot({
              startTime: convertTo24HourFormat(event.startTime),
              endTime: convertTo24HourFormat(event.endTime)
            });
            setOpenBookingDialog(true);
          }
        }
        break;
      case CalendarEventType.EVENT:
        navigate(`/events/${event.id}`);
        break;
      case CalendarEventType.GROUP_PLAY:
        // Route based on user role
        if (user?.role === UserRole.MEMBER) {
          // Members go to booking page
          const groupPlayId = event.metadata?.parentGroupPlayId || event.metadata?.parentGroupPlay?.id;
          if (groupPlayId) {
            navigate(`/member/group-play-booking/${groupPlayId}/${event.id}`, { state: { from: 'calendar' } });
          }
        } else {
          // Admin/Staff/Coach go to instance detail page
          const groupPlayId = event.metadata?.parentGroupPlayId || event.metadata?.parentGroupPlay?.id;
          if (groupPlayId) {
            navigate(`/group-plays/${groupPlayId}/instances/${event.id}`);
          }
        }
        break;
    }
  };

  const getViewTitle = () => {
    // Check if we're in mobile view
    // const isMobile = window.innerWidth < 600; // MUI's 'xs' breakpoint
    
    switch (selectedView) {
      case CalendarViewType.DAILY:
        return selectedDate.toLocaleDateString('en-US', {
                // year: isMobile ? undefined : 'numeric',
                month: 'short',
                day: 'numeric', 
                weekday: 'short'
                });
      case CalendarViewType.WEEKLY:
        const startOfWeek = new Date(selectedDate);
        startOfWeek.setDate(selectedDate.getDate() - selectedDate.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return `${formatDate(formatDateForInput(startOfWeek))} - ${formatDate(formatDateForInput(endOfWeek))}`;
      case CalendarViewType.MONTHLY:
        return selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      default:
        return '';
    }
  };

  return (
    <Container sx={{ px: 0 }}>
      <Box sx={{ mx: { xs: -2, sm: 0 }, mt: { xs: 0, sm: 4 }, mb: { xs: 2, md: 4 } }}>
        {/* Breadcrumbs */}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, mx: { xs: 1, sm: 0 } }}>
          <Button
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            startIcon={<HomeIcon />}
            sx={{ textTransform: 'none' }}
          >
            Dashboard
          </Button>
          <Typography color="text.primary">Calendar</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1, sm: 0 } }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              <CalendarTodayIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
              Calendar
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: { xs: 'none', md: 'block' } }}>
              Keyboard shortcuts: T (Today), J (Jump to date), Ctrl+← / Ctrl+→ (Navigate)
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchCalendarData}
            disabled={loading}
            sx={{
              minWidth: { xs: 'auto', md: 'auto' },
              px: { xs: 1.5, md: 2 },
              '& .MuiButton-startIcon': {
                mr: { xs: 0, md: 1 }
              },
              '& .MuiButton-startIcon + *': {
                display: { xs: 'none', md: 'inline' }
              }
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Controls */}
        <Paper sx={{ p: { xs: 1.5, md: 3 }, mb: 3, mx: { xs: 1, sm: 0 }, borderRadius: { xs: 0, md: 1 } }}>
          <Grid container spacing={{ xs: 1.5, md: 3 }} alignItems="center">
            {/* Calendar Type Selection */}
            <Grid size={{ xs: selectedType === CalendarEventType.AMENITY ? 6 : 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>Calendar Type</InputLabel>
                <Select
                  value={selectedType}
                  label="Calendar Type"
                  onChange={(e) => setSelectedType(e.target.value as CalendarEventType)}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.75rem', md: '1rem' },
                      py: { xs: 0.75, md: 1.5 }
                    }
                  }}
                >
                  {calendarTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {type.icon}
                        <Typography sx={{ ml: 1, fontSize: { xs: '0.75rem', md: '1rem' } }}>{type.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Amenity Selection (only for amenity type) */}
            {selectedType === CalendarEventType.AMENITY && (
              <Grid size={{ xs: 6, md: 3 }}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>Select Amenity</InputLabel>
                  <Select
                    value={selectedAmenityId}
                    label="Select Amenity"
                    onChange={(e) => setSelectedAmenityId(e.target.value)}
                    sx={{
                      '& .MuiSelect-select': {
                        fontSize: { xs: '0.75rem', md: '1rem' },
                        py: { xs: 0.75, md: 1.5 }
                      }
                    }}
                  >
                    {availableAmenities.map((amenity) => (
                      <MenuItem key={amenity.id} value={amenity.id}>
                        <Typography sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>{amenity.name}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            {/* View Type Selection */}
            <Grid size={{ xs: 12, md: 3 }} sx={{ display: { xs: 'none', md: 'block' } }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: { xs: '0.75rem', md: '1rem' } }}>View Type</InputLabel>
                <Select
                  value={selectedView}
                  label="View Type"
                  onChange={(e) => setSelectedView(e.target.value as CalendarViewType)}
                  sx={{
                    '& .MuiSelect-select': {
                      fontSize: { xs: '0.75rem', md: '1rem' },
                      py: { xs: 0.75, md: 1.5 }
                    }
                  }}
                >
                  {viewTypes.map((view) => (
                    <MenuItem key={view.value} value={view.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {view.icon}
                        <Typography sx={{ ml: 1, fontSize: { xs: '0.75rem', md: '1rem' } }}>{view.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Date Navigation */}
            {/* <Grid size={{ xs: 12, md: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={goToToday}
                  startIcon={<TodayIcon />}
                >
                  Today
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={openDatePickerDialog}
                  startIcon={<DateRangeIcon />}
                >
                  Jump
                </Button>
              </Stack>
            </Grid> */}

            {/* Quick Date Navigation */}
            {/* <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getDateRelativeToToday(-1))}
                >
                  Yesterday
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getDateRelativeToToday(1))}
                >
                  Tomorrow
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getStartOfWeek())}
                >
                  Start of Week
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getEndOfWeek())}
                >
                  End of Week
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getStartOfMonth())}
                >
                  Start of Month
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setSelectedDate(getEndOfMonth())}
                >
                  End of Month
                </Button>
              </Box>
            </Grid> */}
          </Grid>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, mx: { xs: 1, sm: 0 } }}>
            {error}
          </Alert>
        )}

        {/* Legend */}
        <Paper sx={{ p: 2, mt: 3, bgcolor: 'grey.50', display: { xs: 'none', md: 'block' }, mx: { xs: 1, sm: 0 } }}>
          <Typography variant="subtitle2" gutterBottom>
            Status Legend
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {selectedType === CalendarEventType.AMENITY ? (
              <>
                <Chip label="Available" size="small" sx={{ bgcolor: '#c8e6c9', color: '#333333' }} />
                <Chip label="Booked" size="small" sx={{ bgcolor: '#ffcdd2', color: '#333333' }} />
                <Chip label="On Hold" size="small" sx={{ bgcolor: '#ffe0b2', color: '#333333' }} />
              </>
            ) : (
              <>
                <Chip label="Active/Scheduled" size="small" sx={{ bgcolor: '#4caf50', color: 'white' }} />
                <Chip label="Cancelled" size="small" sx={{ bgcolor: '#f44336', color: 'white' }} />
                <Chip label="Full" size="small" sx={{ bgcolor: '#ff9800', color: 'white' }} />
                <Chip label="Past" size="small" sx={{ bgcolor: '#9e9e9e', color: 'white' }} />
              </>
            )}
          </Stack>
          {selectedType === CalendarEventType.CLASS && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 Hover over class cards to see detailed information
            </Typography>
          )}
        </Paper>

        {/* Calendar Content */}
        <Paper sx={{ p: { xs: 0, md: 3 }, borderRadius: { xs: 0, md: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: { xs: 'wrap', md: 'nowrap' }, px: { xs: 1, md: 0 }, pt: { xs: 2, md: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: { xs: '1 1 100%', md: '1 1 auto' } }}>
              <IconButton onClick={() => navigateDate('prev')} size="small" sx={{ mr: { xs: 0.5, md: 1 }, p: { xs: 0.5, md: 1 } }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mx: { xs: 0.5, sm: 1, md: 2 }, fontSize: { xs: '0.95rem', sm: '1.25rem', md: '1.5rem' }, whiteSpace: 'nowrap' }}>
                {getViewTitle()}
              </Typography>
              <IconButton onClick={() => navigateDate('next')} size="small" sx={{ ml: { xs: 0.5, md: 1 }, p: { xs: 0.5, md: 1 } }}>
                <ChevronRightIcon />
              </IconButton>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0.5, md: 2 } }}>
                <Button
                  title="Jump to a specific date"
                  size="small"
                  onClick={openDatePickerDialog}
                  sx={{ minWidth: 'auto', px: { xs: 0.5, md: 1 } }}
                >
                  <DateRangeIcon sx={{ fontSize: 'medium' }} />
                  <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'inline', md: 'none' }, fontSize: '0.65rem' }}>
                    Jump
                  </Typography>
                </Button>

                <Button
                  size="small"
                  onClick={goToToday}
                  title="Go to today"
                  sx={{ minWidth: 'auto', px: { xs: 0.5, md: 1 } }}
                >
                  <TodayIcon sx={{ fontSize: 'medium' }} />
                  <Typography variant="caption" sx={{ ml: 0.5, display: { xs: 'inline', md: 'none' }, fontSize: '0.65rem' }}>
                    Today
                  </Typography>
                </Button>
              </Box>
            </Box>
            <Tabs value={selectedView} onChange={(e, newValue) => setSelectedView(newValue)} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {viewTypes.map((view) => (
                <Tab
                  key={view.value}
                  value={view.value}
                  label={view.label}
                  icon={view.icon}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {selectedView === CalendarViewType.DAILY && renderDailyView()}
              {selectedView === CalendarViewType.WEEKLY && renderWeeklyView()}
              {selectedView === CalendarViewType.MONTHLY && renderMonthlyView()}
            </>
          )}
        </Paper>



        {/* Date Picker Dialog */}
        <Dialog open={openDatePicker} onClose={handleDatePickerCancel} maxWidth="xs" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <DateRangeIcon sx={{ mr: 1 }} />
              Jump to Date
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Select Date"
                type="date"
                value={tempSelectedDate}
                onChange={(e) => setTempSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                autoFocus
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDatePickerCancel}>Cancel</Button>
            <Button
              onClick={handleDatePickerConfirm}
              variant="contained"
              disabled={!tempSelectedDate}
            >
              Go to Date
            </Button>
          </DialogActions>
        </Dialog>

        {/* Booking Dialog */}
        <BookAmenityDialog
          open={openBookingDialog}
          onClose={() => {
            setOpenBookingDialog(false);
            setBookingDialogAmenity(null);
            setBookingDialogDate(null);
            setBookingDialogSlot(null);
          }}
          amenity={bookingDialogAmenity}
          onSuccess={(message) => {
            setSuccessMessage(message || 'Booking created successfully!');
            // Refresh calendar data to show the new booking
            fetchCalendarData();
          }}
          preselectedDate={bookingDialogDate}
          preselectedSlot={bookingDialogSlot}
        />

        {/* Success Snackbar */}
        {successMessage && (
          <Alert
            severity="success"
            onClose={() => setSuccessMessage(null)}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9999 }}
          >
            {successMessage}
          </Alert>
        )}
      </Box>
    </Container>
  );
};

export default UniversalCalendar;